import queryString from 'query-string';
import promiseRace from 'lib/promiseRatRace';

import consolv from 'lib/consolv';
// import reportException from 'lib/errorReporter';

// SourceOfTruth adapters
import dtcWebPageAdapter from 'siteAdapters/dtcWebPageAdapter';
import mdManagerAdapter from 'siteAdapters/mdManagerAdapter';

import SourceOfTruth from 'lib/SourceOfTruth';

// Configurations
import generalRules from 'config/rules.json5';
import kronosRules from 'config/kronosRules.json5';
import targetingWhitelist from 'config/targetingWhitelist.json5';
import slotDefinitions from 'config/slotDefinitions.json5';
import configuration from 'config/configuration.json5';
import dfpEnvironment from 'config/dfpEnvironment.json5';
import golfConfiguration from 'config/golf.json5';
import foodComConfiguration from 'config/foodCom.json5';

import slotDefinitionsWeb from 'config/slotDefinitionsWeb.json5';
import slotDefinitionsMobile from 'config/slotDefinitionsMobile.json5';
import slotDefinitionsResponsive from 'config/slotDefinitionsResponsive.json5';

// Other header bidding partner configurations
import amazonA9 from './config/amazonA9.json5';
import indexExchange from './config/indexExchange.json5';

// MCP Modules
import Kronos from './Kronos';
import PhotoGallery from './PhotoGallery';
import setupPrebidHooks from './PrebidAdapter';
import setupMagnitePrebidHooks from './MagnitePrebidAdapter';
import setupAmazonA9Hooks from './AmazonA9Adapter';
import setupIndexExchangeHooks from './IndexExchangeAdapter';
import buildLegacyAdapter from './LegacyAdapter';
import FixedLeaderboard from './FixedLeaderboard';
import lazyLoader from './lazyLoader';
import setupFindAdSlots from './findAdSlots';
import setupFindOutOfPageAdSlots from './findOutOfPageAdSlots';
// import setupBotDetector from './botDetector';
import setupGolfAdditionalSlotMeta from './golfAdditionalSlotMeta';
// import setupAdSlotInteractionDetector from './adSlotInteractionDetector';
import creativeMetaRetriever from './creativeMetaRetriever';
import setupManualRefresh from './manualRefresh';
import setupFetchReporter from './fetchReporter';

import MCP from './MCP';
import buildSlotHierarchy from './buildSlotHierarchy';

const {
  NODE_ENV,
  dfpNetworks,
  libraryName,
  version,
} = buildEnv;

const { sniadsoff, mcpperf } = queryString.parse(window.location.search);

const disableMCP = (sniadsoff !== undefined);

const runPerformanceTest = (mcpperf !== undefined) || NODE_ENV === 'development';
let loadTime;

// TODO: Detect if page is being run inside of an IFRAME and escape if not whitelisted

if (!disableMCP && !window.DatMCP.isLoaded) {
  if (window.performance && runPerformanceTest) {
    loadTime = window.performance.now();
    consolv.log(`DatMCP loaded at ${loadTime / 1000}.`);
  }
  consolv.log(`${libraryName} ${version} ${NODE_ENV} - Initializing`);

  try {
    const rules = [
      ...targetingWhitelist,
      ...slotDefinitions,
      ...slotDefinitionsWeb,
      ...slotDefinitionsMobile,
      ...slotDefinitionsResponsive,
      ...amazonA9,
      ...indexExchange,
      ...kronosRules,
      ...configuration,
      ...golfConfiguration,
      ...foodComConfiguration,
      ...generalRules,
      ...dfpEnvironment,
    ];

    // Determines the page framework and builds the metadata
    const sourceOfTruth = promiseRace([mdManagerAdapter(), dtcWebPageAdapter()])
      .then(adapter => new SourceOfTruth(adapter).ready);

    /* eslint-disable camelcase */
    const filteredRules = sourceOfTruth.then((SoT) => {
      const { config_channel } = SoT.page;
      return rules.filter((rule) => {
        if (rule.channel === undefined || rule.channel === config_channel) return true;
        if (Array.isArray(rule.channel) && rule.channel.indexOf(config_channel) >= 0) return true;
        return false;
      });
    });
    /* eslint-enable camelcase */

    const DatMCP = new MCP({
      rules: filteredRules,
      sourceOfTruth,
      hierarchyFn: buildSlotHierarchy,
      networks: dfpNetworks,
      performanceTest: runPerformanceTest,
    });

    const { executeQueue, loaderInitialized } = window.DatMCP;
    const loaderDatMCP = window.DatMCP;

    window.DatMCP = DatMCP;
    Object.assign(DatMCP, { executeQueue, loaderInitialized });
    window.DatMCP.execute = (callback) => {
      const callbackRes = (typeof callback === 'function') ? callback(window.DatMCP) : undefined;
      return Promise.resolve(callbackRes);
    };

    // Instantiate the ad library extensions
    // setupBotDetector(DatMCP);
    setupGolfAdditionalSlotMeta(DatMCP);
    setupPrebidHooks(DatMCP);
    setupMagnitePrebidHooks(DatMCP);
    setupAmazonA9Hooks(DatMCP);
    setupIndexExchangeHooks(DatMCP);
    // setupAdSlotInteractionDetector(DatMCP);
    setupManualRefresh(DatMCP);
    setupFetchReporter(DatMCP);

    setupFindAdSlots(DatMCP);
    setupFindOutOfPageAdSlots(DatMCP);

    // Maps legacy ad library functions to their new implementations
    buildLegacyAdapter(DatMCP);

    // Sets up the desktop photo gallery functionality
    DatMCP.initializePhotoGallery = (galleryConfiguration) => {
      consolv.log('MCP: PhotoGallery module initialized');
      DatMCP.PhotoGallery = new PhotoGallery(DatMCP, galleryConfiguration);
    };

    // TODO: Temporary until this function is referenced correctly on golfworldlandingpage
    loaderDatMCP.initializePhotoGallery = DatMCP.initializePhotoGallery;

    // Watches user interaction with page and refreshes slots based on defined criteria
    DatMCP.Kronos = new Kronos(DatMCP);
    DatMCP.FixedLeaderboard = new FixedLeaderboard(DatMCP);

    // Pulls creative info from remote API and stores it in the slot's meta data
    creativeMetaRetriever(DatMCP);

    // Loads configured ad slots when they scroll into the viewport
    lazyLoader(DatMCP);

    if (window.performance && runPerformanceTest) {
      DatMCP.addEventListener('ready', () => {
        const timeToLoad = (window.performance.now() - loadTime) / 1000;
        consolv.log(`DatMCP took ${timeToLoad} seconds to load.`);
      });
    }

    // Execute any queued functions that rely on the ad library
    DatMCP.addEventListener('loaded', () => {
      if (Array.isArray(executeQueue)) {
        executeQueue.forEach(([callback, resolve]) => { resolve(callback(DatMCP)); });
      }
    });

    // Debugging overlay for slots
    DatMCP.enableSlotOverlay = () => {
      if (DatMCP.slotOverlay) {
        DatMCP.slotOverlay.enable();
      } else {
        const prefix = buildEnv.libraryRemotePath.indexOf('//') < 0 ? '//' : '';
        const tag = document.createElement('script');
        tag.id = 'mcp-slot-overlay';
        tag.async = true;
        tag.type = 'text/javascript';
        tag.src = `${prefix}${buildEnv.libraryRemotePath}/SlotOverlay.js`;
        document.head.appendChild(tag);
      }

      window.sessionStorage.setItem('mcp-overlay', '1');
    };

    DatMCP.disableSlotOverlay = () => {
      if (DatMCP.slotOverlay) DatMCP.slotOverlay.disable();
      window.sessionStorage.removeItem('mcp-overlay');
    };

    if (window.sessionStorage.getItem('mcp-overlay') === '1') {
      DatMCP.ready.then(() => {
        DatMCP.enableSlotOverlay();
      });
    }
  } catch (e) {
    /* eslint-disable-next-line */
    console.error(e);
    // reportException(e);
  }
}
